import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { FORD_TYPOGRAPHY } from '../theme/fordTheme.js';


// Helper function to convert hex color to rgba with specified alpha
const hexToRgba = (hex, alpha) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = Number.parseInt(result[1], 16);
    const g = Number.parseInt(result[2], 16);
    const b = Number.parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return hex; // fallback to original hex if parsing fails
};

// Individual KPI card
const StyledKpiCard = styled(Box)(({ theme, isFromDashboard, borderColor = '#f39c12' }) => ({
  backgroundColor: hexToRgba(borderColor, 0.08), // Use borderColor with 8% alpha for background
  border: 'none',
  borderLeft: `4px solid ${borderColor}`,
  borderRadius: '8px',
  padding: '16px',
  minWidth: '200px',
  textAlign: 'left',
  flex: '1',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  ...(isFromDashboard && {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '8px 12px',
    textAlign: 'left',
    flexDirection: 'row',
    gap: '0px',
    minWidth: 'auto',
    height: 'auto'
  })
}));

// KPI value styling
const StyledKpiValue = styled(Typography)(({ theme, isFromDashboard, borderColor = '#1e2d5a' }) => ({
  fontSize: '30px',
  fontWeight: '700',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  color: borderColor, // Use borderColor for text color
  lineHeight: '1.2',
  margin: '4px 0',
   ...(isFromDashboard && {
    fontSize: '16px',
    margin: '0px',
    lineHeight: '1'
  })
}));

// KPI label styling (top text)
const StyledKpiLabel = styled(Typography)(({ theme, isFromDashboard }) => ({
  fontSize: '12px',
  color: 'rgb(94, 110, 126)',
  fontWeight: '700',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  lineHeight: '1.2',
  ...(isFromDashboard && {
    fontSize: '14px',
    margin: '0px',
    lineHeight: '1',
    display: 'inline'
  })
}));

// KPI description styling (bottom text) - same styling as label for consistency
const StyledKpiDescription = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: ' rgb(94, 110, 126)',
  fontWeight: '600',
  textTransform: 'none',
  letterSpacing: '0px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  lineHeight: '1.2'
}));

const Kpi = ({ label, value, description, isFromDashboard, borderColor }) => {
  return (
    <StyledKpiCard isFromDashboard={isFromDashboard} borderColor={borderColor}>
      {isFromDashboard ? (
        <>
          <StyledKpiLabel isFromDashboard={isFromDashboard}>{label} - </StyledKpiLabel>
          <StyledKpiValue isFromDashboard={isFromDashboard} borderColor={borderColor}>{value}</StyledKpiValue>
        </>
      ): (
        <>
          <StyledKpiLabel>{label}</StyledKpiLabel>
          <StyledKpiValue borderColor={borderColor}>{value}</StyledKpiValue>
          {description && <StyledKpiDescription>{description}</StyledKpiDescription>}
        </>
      )}
    </StyledKpiCard>
  );
};

// PropTypes validation
Kpi.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  description: PropTypes.string,
  isFromDashboard: PropTypes.bool,
  borderColor: PropTypes.string
};

Kpi.defaultProps = {
  isFromDashboard: false,
};
export default Kpi;
